import React from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { SEVERITY_TYPES, SeverityType, STATUS_COLOURS, StatusColour } from '../../fundamentals/colours/variants';
import { MATERIAL_ICONS, MaterialIcon } from '../Icon/icons';
import { Icon, IIconSharedProps, useIconBaseSizeCss } from '../Icon/Icon';
import { SIZES } from '../../fundamentals/sizes/sizes';
import styled, { css, DefaultTheme } from 'styled-components';
import { SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';
import { ReactComponent as BackgroundCircle } from './BackgroundCircle.svg';
import { ReactComponent as BackgroundTriangle } from './BackgroundTriangle.svg';

/** @deprecated EORA V1 Sizes, avoid using */
export type LegacyStatusIconSize = typeof SIZES.X_MICRO | typeof SIZES.XXX_SMALL | typeof SIZES.XX_SMALL | typeof SIZES.MEDIUM;

export type StatusIconSize =
  | typeof SIZES.MICRO
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.LARGE
  | typeof SIZES.X_LARGE
  | typeof SIZES.XXX_LARGE;

export interface IStatusIconProps extends Omit<IIconSharedProps, 'type' | 'name'> {
  outline?: boolean;
  /**
   * @description LegacyStatusIconSize are deprecated EORA V1 Sizes, avoid using
   */
  size?: StatusIconSize | LegacyStatusIconSize;
  innerFill?: boolean;
}

export enum StatusIconType {
  Default = 'default',
  Outline = 'outline'
}

export const statusTypesToIcons: Record<StatusColour, Record<StatusIconType, MaterialIcon>> = {
  [STATUS_COLOURS.NEUTRAL]: { default: MATERIAL_ICONS.Info, outline: MATERIAL_ICONS.InfoOutline },
  [STATUS_COLOURS.SUCCESS]: { default: MATERIAL_ICONS.CheckCircle, outline: MATERIAL_ICONS.CheckCircleOutline },
  [STATUS_COLOURS.INFO]: { default: MATERIAL_ICONS.Info, outline: MATERIAL_ICONS.InfoOutline },
  [STATUS_COLOURS.WARNING]: { default: MATERIAL_ICONS.Warning, outline: MATERIAL_ICONS.WarningAmber },
  [STATUS_COLOURS.ERROR]: { default: MATERIAL_ICONS.Error, outline: MATERIAL_ICONS.ErrorOutline }
};

type InnerFillProps = Pick<IStatusIconProps, 'size' | 'severityType' | 'outline'>;

const StatusIconBackground = styled(SpaceContainer)<InnerFillProps>`
  display: flex;
  ${useIconBaseSizeCss};
`;

const AbsoluteIcon = styled(Icon)`
  position: absolute;
`;

const getInnerFillColour = (theme: DefaultTheme, severityType: SeverityType, outline: boolean): string => {
  if (severityType === 'white') return outline ? theme.colours.palette.neutral200 : theme.colours.palette.neutral;
  if (severityType === 'black') return outline ? theme.colours.palette.neutral200 : theme.colours.base.white.colour;
  return theme.colours.base.whiteNoSwitch.colour;
};

const useIconInnerFill = css<InnerFillProps>`
  position: absolute;
  fill: ${({ theme, severityType, outline }) => getInnerFillColour(theme, severityType ?? 'coloured', outline ?? false)};
  ${useIconBaseSizeCss};
`;

const BackgroundCircleSvg = styled(BackgroundCircle)<InnerFillProps>`
  ${useIconInnerFill};
`;

const BackgroundTriangleSvg = styled(BackgroundTriangle)<InnerFillProps>`
  ${useIconInnerFill};
`;

export const StatusIcon: React.FC<IStatusIconProps> = ({
  variant = STATUS_COLOURS.NEUTRAL,
  severityType = SEVERITY_TYPES.COLOURED,
  outline,
  size = SIZES.X_SMALL,
  innerFill = false,
  ...rest
}) => {
  const iconName = statusTypesToIcons[variant][outline ? StatusIconType.Outline : StatusIconType.Default];
  const { generateTestId } = useTestId(rest, 'SeverityIcon');

  const commonIconProps = {
    severityType,
    size,
    outline
  };

  if (!innerFill) {
    return <Icon variant={variant} {...commonIconProps} name={iconName} type='round' {...rest} {...generateTestId()} />;
  }

  return (
    <StatusIconBackground {...commonIconProps} {...generateTestId()} {...rest}>
      {variant === 'warning' ? (
        <BackgroundTriangleSvg {...commonIconProps} {...generateTestId('InnerFillTriangle')} />
      ) : (
        <BackgroundCircleSvg {...commonIconProps} {...generateTestId('InnerFillCircle')} />
      )}
      <AbsoluteIcon variant={variant} {...commonIconProps} name={iconName} type='round' {...generateTestId('Icon')} />
    </StatusIconBackground>
  );
};
